import { useEffect, useState } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import { subscribe, unsubscribe } from '@/lib/events';
import { ProductCardType } from '@/components/sider/sider.types';
import type { UseFormSetValue } from 'react-hook-form';

interface UseAgentSiderEventsParams {
  // Search state setters
  setIsArchiveLandingScreen: (v: boolean) => void;
  isArchiveLandingScreenRef: import('react').MutableRefObject<boolean>;
  setSearchValue: (v: string) => void;
  setSelectDataType: (v: any) => void;
  selectDataTypeRef: import('react').MutableRefObject<any>;
  setValue: UseFormSetValue<{ startDate: string; lastDate: string }>;
  setFilterStartDate: (v: string) => void;
  setFilterLastDate: (v: string) => void;
  setPolygonCoordinates: (v: number[][][]) => void;
  polygonCoordinates: number[][][];

  // Map objects
  sketchVM: any;
  view: any;
  previewLayer: any;
  intersectLayer: any;

  // Search execution
  getWebSocketPayload: (coordinates: number[][][]) => void;

  // Cart
  products: ProductCardType[];
  cartItems: any[];
  increaseCartQuantity: (id: string, type: string, data: any, cb: () => void) => void;
  removeFromCart: (id: string) => void;
  resetCart: () => void;
  openCart: () => void;
}

/**
 * Subscribes to AI agent CustomEvents that drive sider behavior:
 * - agent:trigger-search  → sets polygon, dates, data type, triggers search
 * - agent:refine-search   → switches data type or changes date range
 * - agent:cart-action      → add/remove/clear/open cart
 *
 * Returns `agentSearchTrigger` — a counter that increments each time a new
 * agent-initiated search should execute (consumed by the search execution effect).
 */
export function useAgentSiderEvents(params: UseAgentSiderEventsParams) {
  const {
    setIsArchiveLandingScreen,
    isArchiveLandingScreenRef,
    setSearchValue,
    setSelectDataType,
    selectDataTypeRef,
    setValue,
    setFilterStartDate,
    setFilterLastDate,
    setPolygonCoordinates,
    polygonCoordinates,
    sketchVM,
    view,
    previewLayer,
    intersectLayer,
    getWebSocketPayload,
    products,
    cartItems,
    increaseCartQuantity,
    removeFromCart,
    resetCart,
    openCart,
  } = params;

  // Incremented to force a fresh useEffect cycle for search execution
  const [agentSearchTrigger, setAgentSearchTrigger] = useState(0);

  // ── agent:trigger-search ──────────────────────────────────────────
  useEffect(() => {
    const handleAgentSearch = (event: any) => {
      const { coordinates, startDate, endDate, dataType, locationName } = event.detail || {};
      if (!coordinates || !coordinates.length) return;

      // Exit landing screen
      setIsArchiveLandingScreen(false);
      isArchiveLandingScreenRef.current = false;

      // Populate search bar with location name
      if (locationName) {
        setSearchValue(locationName);
      }

      // Update data type
      if (dataType) {
        setSelectDataType(dataType);
        selectDataTypeRef.current = dataType;
      }

      // Update dates — both form values and state
      if (startDate) {
        setValue('startDate', startDate);
        setFilterStartDate(startDate);
      }
      if (endDate) {
        setValue('lastDate', endDate);
        setFilterLastDate(endDate);
      }

      // Set polygon and trigger search via dedicated useEffect
      setPolygonCoordinates(coordinates);
      setAgentSearchTrigger(prev => prev + 1);
    };

    subscribe('agent:trigger-search', handleAgentSearch);
    return () => unsubscribe('agent:trigger-search', handleAgentSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── agent:refine-search (data type switching, date changes) ───────
  useEffect(() => {
    const handleAgentRefine = (event: any) => {
      const { action, data_type, date_range } = event.detail || {};

      if (action === 'switch_data_type' && data_type) {
        const newDt = data_type === 'all' ? ['all'] : [data_type];
        setSelectDataType(newDt);
        selectDataTypeRef.current = newDt;
        // existing useEffect watching [selectDataType] auto-triggers getWebSocketPayload
      }

      if (action === 'change_dates' && date_range) {
        if (date_range.start) {
          setValue('startDate', date_range.start);
          setFilterStartDate(date_range.start);
        }
        if (date_range.end) {
          setValue('lastDate', date_range.end);
          setFilterLastDate(date_range.end);
        }
        // existing useEffect watching [filterStartDate, filterLastDate] auto-triggers getWebSocketPayload
      }
    };

    subscribe('agent:refine-search', handleAgentRefine);
    return () => unsubscribe('agent:refine-search', handleAgentRefine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── agent:cart-action ─────────────────────────────────────────────
  useEffect(() => {
    const handleAgentCartAction = (event: any) => {
      const { action, product_ref, cart_item_ref } = event.detail || {};

      switch (action) {
        case 'add_to_cart': {
          if (!product_ref || products.length === 0) break;
          let matched: ProductCardType | undefined;

          if (product_ref.index && product_ref.index >= 1 && product_ref.index <= products.length) {
            matched = products[product_ref.index - 1];
          } else {
            matched = products.find(p => {
              const srcMatch = product_ref.source
                ? p.source.toLowerCase().includes(product_ref.source.toLowerCase()) ||
                  product_ref.source.toLowerCase().includes(p.source.toLowerCase())
                : true;
              const dateMatch = product_ref.date
                ? p.date?.startsWith(product_ref.date) || p.startDate?.startsWith(product_ref.date)
                : true;
              return srcMatch && dateMatch && (product_ref.source || product_ref.date);
            });
          }

          if (matched) {
            increaseCartQuantity(matched.id, 'Existing', matched, () => {});
          }
          break;
        }
        case 'remove_from_cart': {
          if (!cart_item_ref) break;

          let itemToRemove: any;
          if (cart_item_ref.item_id) {
            itemToRemove = cartItems.find(item => item.id === cart_item_ref.item_id);
          } else if (
            cart_item_ref.index &&
            cart_item_ref.index >= 1 &&
            cart_item_ref.index <= cartItems.length
          ) {
            itemToRemove = cartItems[cart_item_ref.index - 1];
          } else if (cart_item_ref.source) {
            itemToRemove = cartItems.find(
              item =>
                item.cardData?.source?.toLowerCase().includes(cart_item_ref.source.toLowerCase()) ||
                cart_item_ref.source
                  .toLowerCase()
                  .includes(item.cardData?.source?.toLowerCase() || '')
            );
          }

          if (itemToRemove) {
            removeFromCart(itemToRemove.id);
          }
          break;
        }
        case 'clear_cart': {
          resetCart();
          break;
        }
        case 'open_cart': {
          openCart();
          break;
        }
      }
    };

    subscribe('agent:cart-action', handleAgentCartAction);
    return () => unsubscribe('agent:cart-action', handleAgentCartAction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, cartItems]);

  // ── Execute agent-triggered search after state has propagated ─────
  // Runs in a fresh render cycle with current closures for map objects + getWebSocketPayload
  useEffect(() => {
    if (agentSearchTrigger === 0) return;
    if (!polygonCoordinates?.[0]?.[0]?.length) return;

    // Draw the search area on the map and zoom
    try {
      const poly = new Polygon({
        rings: polygonCoordinates,
        spatialReference: { wkid: 4326 },
      });

      previewLayer?.source?.elements?.removeAll();
      sketchVM?.layer?.removeAll();
      intersectLayer?.removeAll();

      if (sketchVM?.polygonSymbol) {
        const graphic = new Graphic({
          geometry: poly,
          symbol: sketchVM.polygonSymbol.clone(),
        });
        sketchVM.layer.add(graphic);
      }

      if (poly.extent) {
        view?.goTo({ target: poly.extent.expand(1.5) }, { duration: 1000 });
      }
    } catch (err) {
      // Map drawing failure doesn't block search
    }

    // Trigger the search — getWebSocketPayload is fresh from this render
    getWebSocketPayload(polygonCoordinates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSearchTrigger]);
}
